import { useState, useEffect, useRef } from "react";
import { X, MessageCircle, Loader2 } from "lucide-react";
import { useChat } from "@/lib/hooks/useChat";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { cn } from "@/components/ui/utils";

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    conversationId,
    messages,
    isLoading,
    isSending,
    error,
    sendMessage,
    initializeConversation,
    clearError,
  } = useChat();

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Initialize conversation when widget opens (only if no conversation exists)
  useEffect(() => {
    if (isOpen && !conversationId && messages.length === 0 && !isLoading) {
      initializeConversation();
    }
  }, [isOpen, conversationId, messages.length, isLoading, initializeConversation]);

  const handleSend = (content: string) => {
    clearError();
    sendMessage(content);
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50",
            "w-14 h-14 rounded-full",
            "bg-blue-600 text-white",
            "shadow-lg hover:shadow-xl",
            "flex items-center justify-center",
            "transition-all hover:scale-110 active:scale-95",
            "focus:outline-none focus:ring-2 focus:ring-blue-500"
          )}
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={cn(
            "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50",
            "w-[calc(100vw-2rem)] sm:w-96 h-[600px] max-h-[calc(100vh-2rem)] rounded-lg shadow-2xl",
            "bg-white dark:bg-gray-800",
            "border border-gray-200 dark:border-gray-700",
            "flex flex-col",
            "animate-in slide-in-from-bottom-4"
          )}
        >
          {/* Header */}
          <div
            className={cn(
              "flex items-center justify-between p-4",
              "border-b border-gray-200 dark:border-gray-700",
              "bg-blue-600 text-white rounded-t-lg"
            )}
          >
            <h2 className="text-lg font-semibold">FAQ Assistant</h2>
            <button
              onClick={() => setIsOpen(false)}
              className={cn(
                "p-1 rounded hover:bg-blue-700",
                "focus:outline-none focus:ring-2 focus:ring-white"
              )}
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div
            className={cn("flex-1 overflow-y-auto p-4", "bg-gray-50 dark:bg-gray-900")}
          >
            {isLoading && messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <p>Ask me anything about Becoming Alpha!</p>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <ChatMessage key={msg.id} role={msg.role} content={msg.content} />
                ))}
                {isSending && (
                  <div className="flex justify-start mb-4">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}

            {error && (
              <div
                className={cn(
                  "mt-2 p-3 rounded-lg",
                  "bg-red-50 dark:bg-red-900/20",
                  "text-red-600 dark:text-red-400",
                  "text-sm"
                )}
              >
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <ChatInput onSend={handleSend} disabled={isSending || isLoading} />
        </div>
      )}
    </>
  );
}
